import { Link, type MetaFunction } from '@remix-run/react'
import { MobileToc, SidebarToc } from '~/components/essay-toc.tsx'
import { Source, SourceModeProvider, SourceModeToggle } from '~/components/source.tsx'
import { Icon } from '~/components/ui/icon.tsx'
import { Code } from '~/ui_components/typography/code.tsx'
import { Em } from '~/ui_components/typography/em.tsx'
import { H2 } from '~/ui_components/typography/h2.tsx'
import { H3 } from '~/ui_components/typography/h3.tsx'
import { Hr } from '~/ui_components/typography/hr.tsx'
import { P } from '~/ui_components/typography/p.tsx'
import { Pre } from '~/ui_components/typography/pre.tsx'
import { Strong } from '~/ui_components/typography/strong.tsx'
import { Ul } from '~/ui_components/typography/ul.tsx'

const sections = [
	{ id: 'throughput', title: 'Throughput' },
	{ id: 'capabilities', title: 'Capabilities' },
	{ id: 'latency', title: 'User-facing Latency' },
	{ id: 'velocity', title: 'Development Velocity' },
	{ id: 'how-to-decide', title: 'How to Decide' },
]

const title = 'Which database should your product actually use? | André Casal'
const description =
	'A first-principles analysis of SQLite vs PostgreSQL. Benchmarks, real company data, and the arithmetic — so you can decide with evidence instead of convention.'

export const meta: MetaFunction = () => [
	{ title },
	{ name: 'description', content: description },
	{ property: 'og:title', content: title },
	{ property: 'og:description', content: description },
	{ property: 'og:url', content: 'https://andrecasal.com/essays/sqlite-vs-postgres' },
	{ property: 'twitter:card', content: 'summary_large_image' },
]

export default function Route() {
	return (
		<SourceModeProvider>
			{/* Back to top */}
			<Link to="#top" className="back-to-top" aria-label="Back to top">
				<Icon name="arrow-up" size="md" />
			</Link>

			{/* Hero */}
			<div id="top" className="pb-16 pt-12 lg:pb-8">
				<Link to="/essays" className="inline-flex items-center gap-2 text-size-sm text-muted-500 transition-colors hover:text-brand">
					<Icon name="arrow-left" size="sm" />
					Essays
				</Link>
				<p className="mt-10 text-size-xs font-semibold uppercase tracking-wider text-brand">Analysis</p>
				<h1 className="mt-3 text-size-4xl font-bold tracking-tight text-muted-900">Which database should your product actually use?</h1>
				<p className="mt-6 max-w-prose text-size-xl leading-relaxed tracking-tight text-muted-600">
					A first-principles analysis of SQLite vs PostgreSQL — with benchmarks, real company data, and the arithmetic.
				</p>
				<div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-size-sm text-muted-400 dark:text-muted-500">
					<span>André Casal</span>
					<span aria-hidden="true">·</span>
					<span>25 min read</span>
					<span aria-hidden="true">·</span>
					<time dateTime="2026-03-19">Mar 19, 2026</time>
				</div>
			</div>

			<MobileToc sections={sections}>
				<SourceModeToggle />
			</MobileToc>

			{/* Article */}
			<div className="article-toc-scope py-16 lg:grid lg:grid-cols-[12rem_minmax(0,65ch)_1fr] lg:gap-8 lg:pt-8">
				<SidebarToc sections={sections}>
					<SourceModeToggle />
				</SidebarToc>
				<div className="essay-body min-w-0">
					{/* ───────────────── Intro ───────────────── */}
					<P>
						"Just use Postgres" is one of the most common pieces of advice in software engineering. But I realized I'd never seen anyone actually
						decompose the question — break down what factors matter, how much they matter, and examine the data for each one. The advice is
						repeated because it sounds right, not because anyone does the analysis.
					</P>
					<P>
						So I did the analysis. I decomposed the question "SQLite or PostgreSQL?" from first principles, ran benchmarks on real databases,
						read the engineering disclosures from companies at scale, and did the arithmetic.
					</P>
					<P>What I found surprised me.</P>

					{/* ───────────────── Throughput ───────────────── */}
					<H2 id="throughput" className="scroll-mt-24">
						Throughput
					</H2>
					<P>"Can my database handle the load?" — let's find out.</P>

					<H3 id="where-the-bottlenecks-really-are" className="scroll-mt-24">
						Where the bottlenecks really are
					</H3>
					<P>
						A request passes through three layers: <Strong>network → application server → database.</Strong> How much traffic can each handle?
					</P>
					<P>
						<Strong>Network.</Strong> Your server's bandwidth depends on your hosting provider. Hosting falls into two categories:{' '}
						<Strong>infrastructure providers</Strong> own and operate physical servers; <Strong>platform providers</Strong> deploy on top of them
						and inherit their network limits.
					</P>
					<P>
						Some infrastructure providers own entire data centers — the building, power, cooling, and network backbone. Others practice{' '}
						<Strong>colocation</Strong>: they own the servers but house them in a third-party facility that supplies power, cooling, and physical
						security. Either way, what matters is the bandwidth allocated to your VM — which providers cap well below the physical NIC's
						capacity:
					</P>
					<Pre className="breakout">
						<Code>{`INFRASTRUCTURE PROVIDERS (own and operate hardware)

┌─ Azure ── servers + data centers ────────────────────────────────────────┐
│  100 Gbps NIC ────────── Azure rate-limits to ──────────► 12.5 Gbps/VM  │
└──────────────────────────────────────────────────────────────────────────┘
┌─ GCP ── servers + data centers ──────────────────────────────────────────┐
│  100 Gbps NIC ────────── GCP rate-limits to ────────────► 8 Gbps/VM     │
└──────────────────────────────────────────────────────────────────────────┘
┌─ Hetzner ── servers + data centers ──────────────────────────────────────┐
│  10 Gbps NIC ── shared across VMs (no per-VM cap) ──────► ~6 Gbps/VM    │
└──────────────────────────────────────────────────────────────────────────┘
┌─ AWS ── servers + data centers ──────────────────────────────────────────┐
│  25 Gbps NIC ────────── AWS rate-limits to ─────────────► 1.56 Gbps/VM  │
└──────────────────────────────────────────────────────────────────────────┘
┌─ Fly.io ── servers in colocation ────────────────────────────────────────┐
│  ~10 Gbps NIC ────────── Fly.io rate-limits to ─────────► ~1.5 Gbps/VM  │
└──────────────────────────────────────────────────────────────────────────┘

WHAT YOUR APP GETS

  Deploy directly (you get a VM — per-VM limit applies):

  Provider          Your app gets
  ────────          ─────────────
  Azure             12.5 Gbps
  GCP               8 Gbps
  Hetzner           ~6 Gbps
  AWS               1.56 Gbps
  Fly.io            ~1.5 Gbps

  Deploy on a platform (upper-bounded by infrastructure NIC):

  Platform          Runs on          Bandwidth
  ────────          ───────          ─────────
  Render (US)       GCP              undocumented (≤ 100 Gbps NIC)
  Render (EU)       AWS              undocumented (≤ 25 Gbps NIC)
  Supabase          AWS              undocumented (≤ 25 Gbps NIC)
  Neon              AWS              undocumented (≤ 25 Gbps NIC)
  Turso             AWS              undocumented (≤ 25 Gbps NIC)
  Vercel            AWS (Lambda)     ~600 Mbps/function (benchmarked)
  Netlify           AWS (Lambda)     ~600 Mbps/function (benchmarked)

All per-VM bandwidth at 4 vCPUs. Platform providers use their own
instance types and architecture — their actual bandwidth is unknown
but cannot exceed their infrastructure provider's physical NIC.`}</Code>
					</Pre>
					<P>
						How many requests per second is that? A dynamic, database-backed request-response — not counting static assets served from CDN —
						carries:
					</P>
					<Ul>
						<li>
							<P>
								<Strong>Request</Strong>: HTTP headers (~<Source href="https://www.chromium.org/spdy/spdy-whitepaper/">800 bytes</Source>) +
								optional POST body (~200–500 bytes). Total: <Strong>~1 KB</Strong>.
							</P>
						</li>
						<li>
							<P>
								<Strong>Response</Strong>: headers (~500 bytes) + body. A JSON API response: median{' '}
								<Source href="https://www.postman.com/state-of-api/">~2–5 KB</Source>. Server-rendered HTML: ~30–40 KB uncompressed,{' '}
								<Strong>
									<Source href="https://httparchive.org/reports/page-weight">~10–15 KB gzipped</Source>
								</Strong>
								.
							</P>
						</li>
						<li>
							<P>
								<Strong>Protocol overhead</Strong> (TCP/IP headers, TLS framing): ~3–5%.
							</P>
						</li>
					</Ul>
					<P>
						Total on the wire: <Strong>~4 KB</Strong> for a JSON API round-trip, <Strong>~14 KB</Strong> for server-rendered HTML.
					</P>

					{/* Network capacity table */}
					<div className="breakout my-space-13 overflow-x-auto">
						<table className="w-full text-size-sm">
							<thead>
								<tr className="border-b border-muted-200">
									<th className="py-3 pr-4 text-left font-semibold">Provider</th>
									<th className="py-3 pr-4 text-left font-semibold">Bandwidth</th>
									<th className="py-3 pr-4 text-right font-semibold">JSON API (~4 KB)</th>
									<th className="py-3 text-right font-semibold">HTML (~14 KB)</th>
								</tr>
							</thead>
							<tbody className="text-muted-700">
								<tr className="border-b border-muted-100">
									<td className="py-3 pr-4">Azure</td>
									<td className="py-3 pr-4">12.5 Gbps</td>
									<td className="py-3 pr-4 text-right">~391K req/s</td>
									<td className="py-3 text-right">~112K req/s</td>
								</tr>
								<tr className="border-b border-muted-100">
									<td className="py-3 pr-4">GCP / Render (US)</td>
									<td className="py-3 pr-4">8 Gbps</td>
									<td className="py-3 pr-4 text-right">~250K req/s</td>
									<td className="py-3 text-right">~71K req/s</td>
								</tr>
								<tr className="border-b border-muted-100">
									<td className="py-3 pr-4">Hetzner</td>
									<td className="py-3 pr-4">~6 Gbps</td>
									<td className="py-3 pr-4 text-right">~188K req/s</td>
									<td className="py-3 text-right">~54K req/s</td>
								</tr>
								<tr className="border-b border-muted-100">
									<td className="py-3 pr-4">AWS / Vercel / Supabase / ...</td>
									<td className="py-3 pr-4">1.56 Gbps</td>
									<td className="py-3 pr-4 text-right">~49K req/s</td>
									<td className="py-3 text-right">~14K req/s</td>
								</tr>
								<tr>
									<td className="py-3 pr-4">Fly.io</td>
									<td className="py-3 pr-4">~1.5 Gbps</td>
									<td className="py-3 pr-4 text-right">~47K req/s</td>
									<td className="py-3 text-right">~13K req/s</td>
								</tr>
							</tbody>
						</table>
					</div>

					<P>
						<Strong>Application server.</Strong> A server running real business logic — authentication, validation, serialization, template
						rendering — handles roughly 10K–50K req/s. Bare-metal benchmarks show 100K+ for trivial handlers (
						<Source href="https://www.techempower.com/benchmarks/">TechEmpower Round 22</Source>), but production workloads with middleware and
						business logic are 5–20× slower.
					</P>

					<P>
						<Strong>Database.</Strong> From the benchmark (methodology and full results below):
					</P>

					{/* Database benchmark table */}
					<div className="breakout my-space-13 overflow-x-auto">
						<table className="w-full text-size-sm">
							<thead>
								<tr className="border-b border-muted-200">
									<th className="py-3 pr-4 text-left font-semibold">Operation</th>
									<th className="py-3 pr-4 text-right font-semibold">SQLite</th>
									<th className="py-3 pr-4 text-right font-semibold">PostgreSQL (1 conn)</th>
									<th className="py-3 text-right font-semibold">PostgreSQL (16 conn)</th>
								</tr>
							</thead>
							<tbody className="text-muted-700">
								<tr className="border-b border-muted-100">
									<td className="py-3 pr-4">Sequential writes</td>
									<td className="py-3 pr-4 text-right">23,403/s</td>
									<td className="py-3 pr-4 text-right">7,740/s</td>
									<td className="py-3 text-right">35,370/s</td>
								</tr>
								<tr>
									<td className="py-3 pr-4">Mixed 80/20 read/write</td>
									<td className="py-3 pr-4 text-right">96,051/s</td>
									<td className="py-3 pr-4 text-right">11,824/s</td>
									<td className="py-3 text-right">—</td>
								</tr>
							</tbody>
						</table>
					</div>

					<P>
						Web apps are 80–97% reads (data from{' '}
						<Source href="https://www.johnnunemaker.com/database-performance-simplified/">GitHub</Source>,{' '}
						<Source href="https://slack.engineering/scaling-datastores-at-slack-with-vitess/">Slack</Source>,{' '}
						<Source href="https://engineering.fb.com/2013/06/25/core-infra/tao-the-power-of-the-graph/">Meta</Source> — detailed below).
					</P>

					<P>Here's how to handle each limit as you hit it.</P>

					<P>
						<Strong>App server CPU saturates.</Strong> Add app servers behind a load balancer. Both databases route all writes to a single
						primary — PostgreSQL natively, SQLite via <a href="https://turso.tech/">Turso/libSQL</a> or{' '}
						<a href="https://github.com/superfly/litefs">LiteFS</a>. The architecture is equivalent.
					</P>
					<P>
						<Strong>Read latency increases.</Strong> Application-level caching (in-memory or Redis), read replicas, CDN for cacheable responses.
						Both databases support all of these. SQLite with embedded replicas has an edge — reads stay in-process on each app server, avoiding
						the network round-trip.
					</P>
					<P>
						<Strong>Write throughput approaches the primary's limit.</Strong> Scale vertically — a faster CPU increases write throughput with
						zero architecture changes. No new processes, no connection pooler, no migration.
					</P>
					<P>
						<Strong>Single-core writes exhausted (SQLite ~23K writes/sec).</Strong> This is the only point where the database choice matters
						for throughput. PostgreSQL can parallelize writes across CPU cores using concurrent connections. But note: PostgreSQL's
						single-connection throughput (~8K writes/sec) is <Em>lower</Em> than SQLite's ~23K — so switching to PostgreSQL only helps if you
						also configure connection pooling and concurrent writers. You're not just changing databases; you're adding infrastructure.
					</P>
					<P>
						Reaching this point requires sustained write volumes that only the most write-heavy applications at 10M+ DAU would generate. At
						every step before this one, SQLite gives you more headroom with less complexity.
					</P>
					<P>
						The rest of this section provides the supporting data: how much throughput products actually need, what companies at scale report,
						and the benchmark numbers behind these claims.
					</P>

					{/* TODO: Remaining subsections — "How much write throughput does a product actually need?", "What real companies actually see", "What each database can handle" */}

					<H2 id="capabilities" className="scroll-mt-24">
						Capabilities
					</H2>
					<P>TODO: Content coming soon.</P>

					<H2 id="latency" className="scroll-mt-24">
						User-facing Latency
					</H2>
					<P>TODO: Content coming soon.</P>

					<H2 id="velocity" className="scroll-mt-24">
						Development Velocity
					</H2>
					<P>TODO: Content coming soon.</P>

					<H2 id="how-to-decide" className="scroll-mt-24">
						How to Decide
					</H2>
					<P>TODO: Content coming soon.</P>

					<Hr />
					<P size="sm" className="text-muted-500">
						<Em>
							Benchmark repository:{' '}
							<a href="https://github.com/andrecasal/sqlite-vs-postgres-benchmark">sqlite-vs-postgres-benchmark</a>
						</Em>
					</P>
					<P size="sm" className="text-muted-500">
						<Em>
							Full company throughput research with citations:{' '}
							<a href="https://github.com/andrecasal/sqlite-vs-postgres-benchmark/blob/main/database-throughput-research.md">
								database-throughput-research.md
							</a>
						</Em>
					</P>
				</div>

			</div>
		</SourceModeProvider>
	)
}
